import React from "react"
import cx from "classnames"
import { useNavigationContext } from "./navigation-context"
import { EXPANDED_CONTENT_WIDTH } from "./navigation-utils"

// =============================================================================
// Props
// =============================================================================

export type NavigationFooterCalloutProps = {
  /** Expanded representation, shown when the sidebar is expanded. */
  children?: React.ReactNode
  /**
   * Compact representation, shown when the sidebar is collapsed. When provided,
   * the callout crossfades between `children` and this node as the sidebar
   * toggles. When omitted, the callout falls back to fading `children` out on
   * collapse.
   */
  collapsed?: React.ReactNode
}

// =============================================================================
// Styles
// =============================================================================

// Crossfade between the expanded and collapsed layers, timed to the sidebar's
// width animation. The `grid-rows` morph animates height without measuring, so
// it doesn't collide with any inner base-ui `Collapsible`'s
// `--collapsible-panel-height`.
const layerStyles =
  "grid min-w-0 overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out"
const layerOpen = "grid-rows-[1fr] opacity-100"
const layerClosed = "grid-rows-[0fr] opacity-0 pointer-events-none"
const layerInner = "overflow-hidden min-h-0"

// =============================================================================
// Component
// =============================================================================

/**
 * Slot in the navigation footer for promoted content (trial indicator, upgrade
 * prompts, etc.). Pins the expanded layer to the expanded sidebar width so it
 * doesn't reflow during the collapse/expand width animation.
 *
 * Pass a `collapsed` node to show a compact representation when the sidebar
 * collapses; the two crossfade. Without it, the expanded content simply fades
 * out on collapse.
 */
export function NavigationFooterCallout({
  children,
  collapsed: collapsedContent
}: NavigationFooterCalloutProps) {
  const { collapsed } = useNavigationContext()

  // The inactive layer stays mounted for the crossfade but must be fully
  // non-interactive and out of the a11y tree (it isn't display:none).
  const inactiveProps = { inert: "", "aria-hidden": true } as const

  return (
    <div data-slot="navigation-footer-callout">
      {/* Expanded layer */}
      <div
        className={cx(layerStyles, collapsed ? layerClosed : layerOpen)}
        {...(collapsed ? inactiveProps : {})}
      >
        <div className={layerInner}>
          <div style={{ width: EXPANDED_CONTENT_WIDTH }}>{children}</div>
        </div>
      </div>

      {/* Collapsed layer */}
      {collapsedContent && (
        <div
          className={cx(layerStyles, collapsed ? layerOpen : layerClosed)}
          {...(collapsed ? {} : inactiveProps)}
        >
          <div className={layerInner}>{collapsedContent}</div>
        </div>
      )}
    </div>
  )
}

NavigationFooterCallout.displayName = "Navigation.FooterCallout"
